/**
 * Centralized API Headers Utility
 *
 * Provides consistent headers for all API requests including:
 * - JWT Authentication via Authorization header
 * - CSRF protection via X-Requested-With header
 * - Content-Type for JSON requests
 *
 * All frontend services should use these utilities for consistent security.
 */

/**
 * JWT tokens are now stored in httpOnly cookies only.
 * This function is deprecated - JWT tokens are not accessible via JavaScript.
 */
export const getAuthToken = (): string | null => {
  // JWT tokens are stored in httpOnly cookies, not accessible via JavaScript
  // Authentication relies on cookie-based auth
  return null;
};

/**
 * Create headers with JWT authentication and CSRF protection
 *
 * The X-Requested-With header is required by the backend CSRF middleware
 * when Origin/Referer headers are not present. This provides defense
 * against cross-site request forgery attacks.
 *
 * @param additionalHeaders - Optional additional headers to include
 * @returns Headers object with auth and CSRF protection
 */
export const getAuthHeaders = (
  additionalHeaders: Record<string, string> = {},
): Record<string, string> => {
  const headers: Record<string, string> = {
    "X-Requested-With": "XMLHttpRequest", // CSRF protection header
    ...additionalHeaders,
  };

  // JWT tokens are now stored in httpOnly cookies, not included in headers
  // Authentication relies on cookie-based auth with credentials: 'include'

  return headers;
};

/**
 * Get headers for JSON requests with authentication and CSRF protection
 *
 * @param additionalHeaders - Optional additional headers to include
 * @returns Headers object with Content-Type, auth, and CSRF protection
 */
export const getJsonAuthHeaders = (
  additionalHeaders: Record<string, string> = {},
): Record<string, string> => {
  return getAuthHeaders({
    "Content-Type": "application/json",
    ...additionalHeaders,
  });
};

/**
 * Get headers for requests without authentication (but with CSRF protection)
 * Use this for public endpoints that still need CSRF protection.
 *
 * @param additionalHeaders - Optional additional headers to include
 * @returns Headers object with CSRF protection
 */
export const getCsrfHeaders = (
  additionalHeaders: Record<string, string> = {},
): Record<string, string> => {
  return {
    "X-Requested-With": "XMLHttpRequest",
    ...additionalHeaders,
  };
};

/**
 * Get headers for JSON requests without authentication (but with CSRF protection)
 *
 * @param additionalHeaders - Optional additional headers to include
 * @returns Headers object with Content-Type and CSRF protection
 */
export const getJsonCsrfHeaders = (
  additionalHeaders: Record<string, string> = {},
): Record<string, string> => {
  return getCsrfHeaders({
    "Content-Type": "application/json",
    ...additionalHeaders,
  });
};


